import { pickScore } from '@upright/common/model/output/scores';
import { allValueChains, ValueChain } from '@upright/common/model/types';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import { PlainIconFigureBox } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/common/figure-box';
import { friendlyValueChain } from '@upright/ui/untidy/texts';
import { maxBy } from 'lodash';
import styled from 'styled-components';
import Icon from '@streamlinehq/streamlinehq/img/manual/hierarchy.svg';

export function TopValueChain() {
  const context = useNetImpactLensContext();

  if (!context) {
    return <></>;
  }

  const data = context.data;
  const selection = context.state;
  const valence = selection.valence;
  const impact = selection.impact;
  const scores = data?.scores;

  if (!valence || !impact || !scores) {
    return <></>;
  }

  const topValueChain = maxBy(allValueChains, vc => pickScore(scores, impact, vc, valence));
  const topValueChainScore = pickScore(scores, impact, topValueChain, valence);
  const totalScore = pickScore(scores, impact, undefined, valence);
  const share = topValueChainScore / totalScore;
  const text = (share * 100).toFixed(0) + '%';

  if (!topValueChain) {
    return <></>;
  }

  return (
    <PlainIconFigureBox
      icon={Icon}
      numberText={text}
      textBelowNumber={friendlyValueChain(topValueChain).toLowerCase()}
      textAboveNumber={null}
      explanationText={getValueChainExplanationText(topValueChain, share)}
    />
  );

  return <></>;
}

const Root = styled.div`
  margin-bottom: 1em;
  line-height: 1.4;
`;

function getValueChainExplanationText(topValueChain: ValueChain, share: number) {
  switch (topValueChain) {
    case ValueChain.DOWNSTREAM:
      return (
        <Root>
          <b>{(share * 100).toFixed(0)}%</b> of the impact is created <b>downstream</b>, i.e. when its products and
          services are used.
        </Root>
      );
    case ValueChain.UPSTREAM:
      return (
        <Root>
          <b>{(share * 100).toFixed(0)}%</b> of the impact is created <b>upstream</b>, i.e. by its suppliers.
        </Root>
      );
    case ValueChain.INTERNAL:
      return (
        <Root>
          <b>{(share * 100).toFixed(0)}%</b> of the impact is created <b>internally</b>, i.e. as part of the production
          or provision of its products and services.
        </Root>
      );
    default:
      return <></>;
  }
}
